import {
  locationConsumption,
  canteenConsumption,
  stpTreatment,
  etpTreatment,
} from '../../utils/waterDerivations';

/**
 * The ten charts of the Water Management Dashboard, in the order the page
 * shows them.
 *
 * Every title and formula is worded exactly as the page words it today; this
 * is a visual rebuild, and none of these numbers changes with it. The three
 * that carry a `derive` are the ones no meter reports directly.
 *
 * `graphType` picks the mark. A single per-period total draws as bars, because
 * a line between two daily figures implies readings between them; several
 * series compared over the same periods draw as lines, because the eye can
 * follow a line and cannot follow eight groups of three bars.
 *
 * One further chart, Meter-wise Canteen Water Consumption, is commented out in
 * the page today and stays that way.
 */
/**
 * Namespace the insights cache is scoped under.
 *
 * The cache is keyed `tab::graphId`, and every dashboard in the product shares
 * one module-level map. Without a namespace of its own, a chart here whose id
 * matched one on the BI dashboard would be served that page's narrative.
 */
export const INSIGHTS_TAB = 'water-dashboard';

export const WATER_CHARTS = [
  {
    id: 'ground-water-withdrawal',
    title: 'Total Ground Water Withdrawal',
    category: 'Withdrawal',
    subCategory: 'Ground Water',
    kpi: 'Borewell',
    graphType: 'periodTotal',
  },
  {
    id: 'borewell-wise-withdrawal',
    title: 'Borewell-wise Groundwater Withdrawal',
    category: 'Withdrawal',
    subCategory: 'Ground Water',
    kpi: 'Borewell',
    stackByField: 'meter_id',
    graphType: 'seriesComparison',
  },
  {
    id: 'ground-water-consumption',
    title: 'Total Ground Water Consumption (From Borewell to Admin Top)',
    category: 'Consumption',
    subCategory: 'Ground Water',
    kpi: 'From Borewell to Admin',
    graphType: 'periodTotal',
  },
  {
    id: 'location-wise-consumption',
    title: 'Location-wise Borewell Water Consumption',
    formula: 'KIL = From Borewell to Admin - (GES + Sports Club)',
    category: 'Consumption',
    subCategory: 'Ground Water',
    stackByField: 'kpi',
    derive: locationConsumption,
    graphType: 'seriesComparison',
  },
  {
    id: 'canteen-consumption',
    title: 'Total Water Consumption – Canteen',
    formula: 'Canteen Consumption = 11 Canteen + Awanthi 50 ppm - GES',
    kpi: ['Canteen + GES', 'Awanthi 50 ppm', 'GES'],
    stackByField: 'kpi',
    derive: canteenConsumption,
    graphType: 'periodTotal',
  },
  {
    id: 'tanker-water',
    title: 'Total Tanker Water Withdrawal/Consumption',
    category: 'Consumption',
    subCategory: 'Third Party Tanker Water',
    graphType: 'periodTotal',
  },
  {
    id: 'tanker-water-by-kpi',
    title: 'Total Tanker Water Withdrawal/Consumption By KPI',
    category: 'Consumption',
    subCategory: 'Third Party Tanker Water',
    stackByField: 'kpi',
    graphType: 'seriesComparison',
  },
  {
    id: 'total-treated-water',
    title: 'Total Treated Water',
    category: 'Treated Water',
    subCategory: 'Recycled',
    kpi: 'Final Treated (STP + ETP)',
    graphType: 'periodTotal',
  },
  {
    id: 'treated-water-etp-outlet',
    title: 'Total Treated Water (ETP Outlet)',
    category: 'Treated Water',
    subCategory: 'Recycled',
    kpi: 'ETP',
    graphType: 'periodTotal',
  },
  {
    id: 'treatment-wise-stp',
    title: 'Treated Vs Untreated STP Water',
    kpi: ['STP (Untreated)', 'ETP', 'Final Treated (STP + ETP)'],
    stackByField: 'kpi',
    derive: stpTreatment,
    graphType: 'seriesComparison',
  },
  {
    id: 'treatment-wise-etp',
    title: 'Treated Vs Untreated ETP Water',
    kpi: ['ETP (Untreated)', 'ETP'],
    stackByField: 'kpi',
    derive: etpTreatment,
    graphType: 'seriesComparison',
  },
];
